/**
 * EventsListPage
 *
 * Lists Hub Meetings and Training/Workshops scheduled in the current CBO.
 * Visible to all roles (events are program-wide); attendance counts are
 * shown for everyone, but the per-promotora breakdown is gated to
 * supervisor+ via the separate /api/calendar/attendance-report endpoint
 * surfaced in Phase 4c's Reports tab.
 *
 * Promotoras can click into an event to view attendees and self-mark
 * their own attendance (uses the relaxed POST /events/{id}/attendees
 * gate from Phase 2a).
 */

import React, { useEffect, useState } from 'react';
import { Plus, Calendar, Filter, MapPin, Users, Clock, Video, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { calendarAPI, CalendarEvent, EventType } from '@/services/api/calendar';
import { EventDetailPage } from './EventDetailPage';
import { useTabbedChrome } from '@/components/weavein';

interface EventsListPageProps {
  onCreateClick?: () => void;
  /** Wired to the app-shell tab switcher so users can jump to the main
   *  Calendar view from the Activity Tracking list. App.tsx passes
   *  `(tab) => navigateWithHistory(tab)`. */
  onNavigate?: (tab: string) => void;
  /** When set, the page renders the EventDetailPage immediately instead of
   *  the list. Used by Calendar.tsx via sessionStorage cross-tab hand-off
   *  so a click on a Hub/Training event in /calendar lands on the detail
   *  view inside Activity Tracking. */
  initialEventId?: number | null;
}

const SOMOS_EVENT_TYPES: EventType[] = ['network_hub_meeting', 'training_workshop'];

export const EventsListPage: React.FC<EventsListPageProps> = ({ onCreateClick, onNavigate, initialEventId }) => {
  const tabbed = useTabbedChrome();
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<EventType | ''>('');
  const [startDate, setStartDate] = useState<string>(() => firstOfMonthIso());
  const [endDate, setEndDate] = useState<string>(() => lastOfMonthIso());
  const [openEventId, setOpenEventId] = useState<number | null>(initialEventId ?? null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setError(null);

    // The events list endpoint takes one event_type at a time; we either
    // pass the filtered type or fan out across both Somos types and merge.
    const typesToFetch: EventType[] = typeFilter ? [typeFilter] : SOMOS_EVENT_TYPES;
    Promise.all(
      typesToFetch.map((t) =>
        calendarAPI.getEvents({
          start_date: startDate,
          end_date: endDate,
          event_type: t,
          page_size: 200,
        }),
      ),
    )
      .then((responses) => {
        if (cancelled) return;
        const merged = responses
          .flatMap((r: any) => r.events || r || [])
          .filter((e) => e && SOMOS_EVENT_TYPES.includes(e.event_type as EventType))
          .sort((a, b) => (a.start_time < b.start_time ? 1 : -1));
        setEvents(merged);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load events.');
      });
    return () => {
      cancelled = true;
    };
  }, [typeFilter, startDate, endDate, reloadKey]);

  // Full-screen takeover: when an event is selected, hand the entire viewport
  // to the EventDetailPage. The list view rehydrates on back-navigation via
  // reloadKey so attendee changes / archives reflect immediately.
  if (openEventId != null) {
    return (
      <EventDetailPage
        eventId={openEventId}
        onBack={() => {
          setOpenEventId(null);
          setReloadKey((k) => k + 1);
        }}
        onMutated={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-2">
        <div>
          {!tabbed && (
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-600" />
            Hub Meetings &amp; Trainings
          </h1>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Internal staff attendance at hub meetings and trainings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onNavigate && (
            <Button variant="outline" onClick={() => onNavigate('calendar')}>
              <Calendar className="w-4 h-4 mr-2" />
              View on calendar
            </Button>
          )}
          {onCreateClick && (
            <Button onClick={onCreateClick}>
              <Plus className="w-4 h-4 mr-2" />
              Schedule event
            </Button>
          )}
        </div>
      </header>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as EventType | '')}
              className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="network_hub_meeting">Hub meetings</option>
              <option value="training_workshop">Trainings &amp; workshops</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">From</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">To</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-800">{error}</CardContent>
        </Card>
      )}

      {events === null && !error ? (
        <div className="text-sm text-gray-500 py-8 text-center">Loading…</div>
      ) : events && events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-gray-500">
            No events scheduled in this range.{' '}
            {onCreateClick && (
              <button onClick={onCreateClick} className="text-blue-600 underline ml-1">
                Schedule one.
              </button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {events!.map((evt) => (
            <EventRow key={evt.id} evt={evt} onOpen={() => setOpenEventId(evt.id!)} />
          ))}
        </div>
      )}

    </div>
  );
};

const EventRow: React.FC<{ evt: CalendarEvent; onOpen: () => void }> = ({ evt, onOpen }) => {
  const start = evt.start_time ? new Date(evt.start_time) : null;
  const end = evt.end_time ? new Date(evt.end_time) : null;
  const minutes = start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;
  const fmtTime = start
    ? start.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
  const attendeeCount =
    (evt as any).attendee_count ?? (evt.attendees ? evt.attendees.length : 0);
  const subtype = (evt as any).custom_fields?.activity_subtype;
  const isVirtual = evt.location_type === 'virtual' || evt.location_type === 'phone';

  return (
    <Card>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={onOpen}
          className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
        >
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{evt.title}</span>
                <Badge variant="outline" className="text-xs">
                  {evt.event_type === 'network_hub_meeting' ? 'Hub' : 'Training'}
                </Badge>
                {subtype && (
                  <Badge variant="outline" className="text-xs">
                    {subtype}
                  </Badge>
                )}
                {evt.status === 'cancelled' && (
                  <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                    Cancelled
                  </Badge>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1 flex gap-3 flex-wrap items-center">
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {fmtTime}
                  {minutes != null && ` · ${minutes} min`}
                </span>
                <span className="inline-flex items-center gap-1">
                  {isVirtual ? <Video className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                  {isVirtual ? 'Virtual' : 'In-Person'}
                </span>
                {evt.location_address && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {evt.location_address}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {attendeeCount} attendee{attendeeCount === 1 ? '' : 's'}
                </span>
              </div>
              {evt.description && (
                <div className="text-xs text-gray-600 mt-1 line-clamp-2">{evt.description}</div>
              )}
            </div>
            <div className="text-xs text-blue-600 flex-shrink-0 self-center">Open</div>
          </div>
        </button>
      </CardContent>
    </Card>
  );
};

function firstOfMonthIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
}

function lastOfMonthIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
}

export default EventsListPage;
